import { getCurrentUser } from 'vuefire'
import { memberIsAdmin, memberIsBandAdmin, memberIsRedshirtAdmin } from '../shared/utils/memberUtils.js'
import { getRedirectUserIfPresent, useAuthReady } from '~/composables/useAuthReady.js'

const allowedRoutes = ['/login', '/signup', '/logout', '/forgot-password', '/privacy-policy', '/terms-of-service', '/delete-account', '/about', '/beacon42', '/discourse-sso', '/maintenance']

export default defineNuxtRouteMiddleware(async (to, _from) => {
  if (to.path === '/logout') {
    return true
  }

  // Always wait for auth to be ready - this handles redirect results from Google sign-in
  // and ensures auth state is fully initialized before checking user
  await useAuthReady()

  // If we just completed a redirect sign-in, navigate appropriately
  // This ensures clean state initialization after OAuth redirect
  const redirectUser = getRedirectUserIfPresent()
  // /login and /signup are the same page; Google and Apple come back to either
  if (redirectUser && (to.path === '/login' || to.path === '/signup')) {
    // Check for redirect query param (e.g., from Discourse SSO)
    const redirectParam = to.query?.redirect as string | undefined
    if (redirectParam) {
      // Validate it's a trusted URL before redirecting
      const trustedHosts = [
        'krewebacchus.org',
        'www.krewebacchus.org',
        'krewebacchus.web.app',
        'dis.krewebacchus.org',
        'us-central1-krewebacchus.cloudfunctions.net',
      ]
      try {
        const url = new URL(redirectParam)
        if (trustedHosts.includes(url.host)) {
          return navigateTo(redirectParam, { external: true })
        }
      }
      catch {
        // Invalid URL, fall through to default
      }
    }
    return navigateTo('/', { external: true })
  }

  const user = redirectUser ?? await getCurrentUser()

  // System config (live, stores/systemConfig.ts) loads alongside the member
  // record below rather than after it: each read used to wait on the last
  const systemConfigStore = useSystemConfigStore()
  const configLoaded = systemConfigStore.loadConfig()

  if (!user) {
    if (!allowedRoutes.includes(to.path)) {
      return navigateTo('/login')
    }
    return true
  }

  // Skip session check for redirect users - we just created the session in useAuthReady
  if (!redirectUser) {
    const { checkSession, refreshSessionStatus } = useAuthSession()

    // Sign out only when Firebase or the server says the session is no good.
    // Offline (a check-in tablet at the parade) the refresh just fails, and
    // that used to sign the volunteer out
    if (!await checkSession() && await refreshSessionStatus() === 'unauthorized') {
      return navigateTo('/logout')
    }
  }

  const emailVerified = user.emailVerified ?? true // Default to true for redirect users

  if (to.path === '/verify-email' && user && !emailVerified) {
    return true
  }

  const memberData = await useCurrentMember(user.uid)

  // An unverified email account finishes signing up first (fewer accounts
  // stuck as pending), then confirms its address. The signup flow itself
  // asks for the confirmation before anything tied to the address (claiming
  // a SubKrewe as its expected captain, lifetime status).
  if (user && !emailVerified) {
    const signingUp = !memberData?.member_type || memberData.member_type === 'pending'
    if (signingUp) {
      return to.path === '/signup-flow' ? true : navigateTo('/signup-flow')
    }
    return navigateTo('/verify-email')
  }

  if (!memberData) {
    return false
  }

  // Maintenance mode: redirect non-admins to maintenance page
  await configLoaded
  const isEvenflowUser = user.email?.endsWith('@evenflow.nl') ?? false
  const isBetaTester = memberData.beta_tester === true
  const maintenanceMode = systemConfigStore.config?.maintenanceMode ?? false
  if (maintenanceMode && !memberIsAdmin(memberData) && !isEvenflowUser && !isBetaTester && to.path !== '/maintenance') {
    return navigateTo('/maintenance')
  }

  // Redirect to signup flow if user hasn't completed registration. Not from
  // /maintenance: during maintenance a pending member bounced between the
  // two pages forever (the check above runs first now, and this lets them stay)
  if ((!memberData.member_type || memberData.member_type === 'pending')
    && to.path !== '/signup-flow' && to.path !== '/maintenance') {
    return navigateTo('/signup-flow')
  }

  // Check for admin routes
  if (to.path.startsWith('/admin')) {
    // Check if user has admin role
    const admin = memberIsAdmin(memberData)
    const bandAdmin = memberIsBandAdmin(memberData)
    const redshirtAdmin = memberIsRedshirtAdmin(memberData)

    // Special case for /admin/subkrewes - allow band admins
    if (to.path === '/admin/subkrewes') {
      // Allow full admins, band admins, and redshirt admins (limited capabilities enforced in UI/Rules)
      if (!admin && !bandAdmin && !redshirtAdmin) {
        return navigateTo('/_error?code=403')
      }
    }
    // Allow redshirt_admins into members overview
    else if (to.path === '/admin/members') {
      if (!admin && !redshirtAdmin) {
        return navigateTo('/_error?code=403')
      }
    }
    // Allow redshirt_admins into captains overview
    else if (to.path === '/admin/captains') {
      if (!admin && !redshirtAdmin) {
        return navigateTo('/_error?code=403')
      }
    }
    // Allow redshirt_admins into lineup (read-only lieutenant assignment)
    else if (to.path === '/admin/lineup') {
      if (!admin && !redshirtAdmin) {
        return navigateTo('/_error?code=403')
      }
    }
    // Allow redshirt_admins and lieutenants into lineup-status (lined-up tracking)
    else if (to.path === '/admin/lineup-status') {
      const isLieutenant = memberData?.isRedshirtLieutenant === true
      if (!admin && !redshirtAdmin && !isLieutenant) {
        return navigateTo('/_error?code=403')
      }
    }
    // Allow redshirt_admins and lieutenants into radio-list
    else if (to.path === '/admin/radio-list') {
      const isLieutenant = memberData?.isRedshirtLieutenant === true
      if (!admin && !redshirtAdmin && !isLieutenant) {
        return navigateTo('/_error?code=403')
      }
    }
    // Allow redshirt_admins into inventory management
    else if (to.path === '/admin/inventory') {
      if (!admin && !redshirtAdmin) {
        return navigateTo('/_error?code=403')
      }
    }
    else {
      // For all other admin routes, require full admin access
      if (!admin) {
        return navigateTo('/_error?code=403')
      }
    }

    // The admin live set (every member, SubKrewe, product, ...): sign-in
    // starts it too, this gets it going on a first load straight onto an
    // admin page. The pages wait for what they need themselves.
    if (admin || bandAdmin || redshirtAdmin) {
      useAdminLiveStore().start()
    }
  }

  return true
})
